'use client';

import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Alert, Button, IconButton, Snackbar } from '@mui/material';

type SnackbarPosition = {
  vertical: 'top' | 'bottom';
  horizontal: 'left' | 'center' | 'right';
};

type Severity = 'success' | 'error' | 'warning' | 'info';

type SnackbarContextType = {
  showSnackbar: (
    message: string,
    options?: {
      duration?: number;
      anchorOrigin?: SnackbarPosition;
      severity?: Severity;
      actionLabel?: string;
      onActionClick?: () => void;
    }
  ) => void;
};

const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined
);

export const SnackbarProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<Severity>('success');
  const [duration, setDuration] = useState(6000);
  const [anchorOrigin, setAnchorOrigin] = useState<SnackbarPosition>({
    vertical: 'top',
    horizontal: 'right',
  });
  const [actionLabel, setActionLabel] = useState<string | undefined>();
  const [onActionClick, setOnActionClick] = useState<
    (() => void) | undefined
  >();

  const showSnackbar: SnackbarContextType['showSnackbar'] = useCallback(
    (msg, options) => {
      setMessage(msg);
      setSeverity(options?.severity ?? 'success');
      setDuration(options?.duration ?? 6000);
      setAnchorOrigin(
        options?.anchorOrigin ?? { vertical: 'top', horizontal: 'right' }
      );
      setActionLabel(options?.actionLabel);
      setOnActionClick(() => options?.onActionClick);
      setOpen(true);
    },
    []
  );

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const value = useMemo(() => ({ showSnackbar }), [showSnackbar]);

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <Snackbar
        anchorOrigin={anchorOrigin}
        open={open}
        autoHideDuration={duration}
        onClose={handleClose}
        slotProps={{
          root: {
            sx: {
              top: 120,
            },
          },
        }}
      >
        <Alert
          severity={severity}
          onClose={handleClose}
          action={
            <>
              {actionLabel && (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    onActionClick?.();
                    handleClose();
                  }}
                >
                  {actionLabel}
                </Button>
              )}
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleClose}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          }
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within SnackbarProvider');
  }
  return context;
};
